import { CatCustomerRatingContext } from "@/context/CatCustomerRatingContext"
import {
  isCatReviewPage,
  parseCatCustomerRating,
} from "@/lib/reviews/cat-customer-rating"
import type { CatCustomerRating } from "@/lib/reviews/cat-customer-rating"
import { sanitizeCatReviewSnippet } from "@/seo/cat-review-cleanup"
import { fetchReviews } from "@/util/reviews"
import {
  resolveExperienceYears,
  resolveShipmentContent,
} from "@/lib/completed-shipments/content"
import {
  getShipmentConcept,
  hasExperienceYearsOnly,
} from "@/lib/completed-shipments"
import {
  getCompletedShipmentStats,
  SHIPMENT_STATS_REVALIDATE,
} from "@/lib/completed-shipments/server"
import * as config from "../util/config"

import {
  StoryblokComponent,
  apiPlugin,
  ISbStoryData,
  storyblokInit,
  setComponents,
  useStoryblokState,
} from "@storyblok/react"
import { GetStaticPaths, GetStaticProps } from "next"
import dynamic from "next/dynamic"

import PageHead from "../components/head/PageHead"
import React from "react"
import { ReviewsContextProvider } from "@/context/ReviewsContext"
import components from "../storyblok"
import { getStoryblokRuntimeAccessToken } from "../config/storyblok-access-token"
import {
  fetchStoryblokPage,
  isStoryblokNotFoundError,
} from "@/lib/storyblok-pages"
import { isDraftStoryblokMode } from "@/lib/storyblok-cache-version"
import { sanitizeStoryblokRichSnippetContent } from "@/seo/storyblok-rich-snippets"
import { REVIEW_SEGMENTS } from "@/lib/reviews/segments"
import type { ReviewSegmentDefinition } from "@/lib/reviews/segments"
import { REVIEW_ARCHIVE_REVALIDATE_SECONDS } from "@/lib/reviews/constants"
import { ReviewSegmentServiceLink } from "@/components/reviews/ReviewSegmentLinks"
import type { SenjaVideoTestimonial } from "@/lib/senja-video-testimonials"
import type { SenjaCachedRenderBatch } from "@/lib/senja-video-testimonials-cache"
import { getSenjaTestimonialsForCachedRender } from "@/lib/senja-video-testimonials-cache"
import {
  SENJA_PAGE_REVALIDATE_SECONDS,
  SENJA_WIDGET_GROUPS,
  senjaGroupForPath,
  senjaRevalidateSeconds,
} from "@/lib/senja-widgets"
import type { SenjaWidgetGroupKey } from "@/lib/senja-widgets"
import { hasSenjaBlok } from "@/lib/senja-blok-tree"
import { inspectSenjaStory } from "@/lib/senja-story-groups"
import { STORYBLOK_PAGE_MARKER } from "@/constants/markdown"
import { SenjaTestimonialsContext } from "@/storyblok/sections/senja-testimonials-context"
import type { SenjaTestimonialsByGroup } from "@/storyblok/sections/senja-testimonials-context"

// Only the stories in SENJA_PAGE_GROUPS render this section here, so keep it out of
// the client bundle of the homepage and every other CMS page. Called directly
// with a literal import() so Next lists the chunk in `dynamicIds` and the
// server-rendered section hydrates (see "Hydration safety" in
// .claude/rules/programmatic-pages.md).
const SenjaVideoTestimonials = dynamic(() =>
  import("@/components/senja/SenjaVideoTestimonials").then(
    (module) => module.SenjaVideoTestimonials,
  ),
)

storyblokInit({
  accessToken: getStoryblokRuntimeAccessToken(),
  use: [apiPlugin],
  apiOptions: {
    region: "us",
  },
  components,
})

const REDIRECT_PATHS = ["shipments", "new", "login", "register", "password"]

const shouldRedirect = (slug: string[]): boolean => {
  return slug.some((segment) => REDIRECT_PATHS.includes(segment))
}

const getRedirectDestination = (slug: string[]): string => {
  const host = config.getAppHost()

  return `${host}/${slug.join("/")}`
}

function resolveStoryContent<T>(
  content: T,
  slug: string,
  completedShipments: Props["completedShipments"],
  reviews: Record<string, string> | null,
): T {
  const concept = getShipmentConcept(slug)

  if (concept) {
    return resolveShipmentContent(content, completedShipments, concept, reviews)
  }

  return hasExperienceYearsOnly(slug)
    ? resolveExperienceYears(content)
    : content
}

const NO_BLOK_TESTIMONIALS: SenjaTestimonialsByGroup = {}

// Draft deployments (stage, PR previews, preprod) regenerate on a short window
// so saved drafts show up without a manual revalidation. The browser cannot
// refresh them itself: it only holds the published-only public token.
const DRAFT_PAGE_REVALIDATE_SECONDS = 60

// Use the shortest freshness window across optional sections.
function pageRevalidateSeconds(
  isSegmentService: boolean,
  senjaBatches: SenjaCachedRenderBatch[],
  hasShipmentProof: boolean,
): number | undefined {
  const windows = [
    isDraftStoryblokMode() ? DRAFT_PAGE_REVALIDATE_SECONDS : undefined,
    isSegmentService ? REVIEW_ARCHIVE_REVALIDATE_SECONDS : undefined,
    hasShipmentProof ? SHIPMENT_STATS_REVALIDATE : undefined,
    ...senjaBatches.map((batch) =>
      senjaRevalidateSeconds(batch.isCurrent, SENJA_PAGE_REVALIDATE_SECONDS),
    ),
  ].filter((seconds): seconds is number => seconds !== undefined)

  return windows.length > 0 ? Math.min(...windows) : undefined
}

type Props = {
  catCustomerRating?: CatCustomerRating | null
  completedShipments: import("@/lib/completed-shipments").ShipmentStats | null
  story: ISbStoryData<StoryblokPageContent> | null
  storySlug: string
  preview?: boolean
  key: number | string | null
  isHomePage?: boolean
  reviewSegment?: ReviewSegmentDefinition | null
  senjaTestimonials?: SenjaVideoTestimonial[]
  senjaWidgetId?: string
  senjaHeading?: string
  senjaByGroup?: SenjaTestimonialsByGroup
}

type StoryblokRichSnippet = {
  content: string
}

type StoryblokPageContent = {
  component?: string
  title?: string
  description?: string
  customScript?: string
  richSnippets?: StoryblokRichSnippet[]
  [key: string]: unknown
}

function hasPageStoryContent(
  story: unknown,
): story is ISbStoryData<StoryblokPageContent> {
  if (typeof story !== "object" || story === null) {
    return false
  }

  if (!("content" in story)) {
    return false
  }

  const { content } = story

  return typeof content === "object" && content !== null
}

const Page: React.FC<Props> = ({
  story,
  storySlug,
  catCustomerRating,
  preview,
  isHomePage = false,
  reviewSegment,
  senjaTestimonials,
  senjaWidgetId,
  senjaHeading,
  senjaByGroup,
}) => {
  // The App Router also initializes Storyblok. Bind this render to the Pages
  // registry so CMS blocks share this page's review and shipment contexts.
  setComponents(components)
  const resolvedStory = useStoryblokState(story, {})

  if (!hasPageStoryContent(resolvedStory)) {
    return null
  }

  const { content } = resolvedStory
  const isCatPage = isCatReviewPage(storySlug, resolvedStory.id)
  const catRating = isCatPage ? catCustomerRating ?? null : undefined
  // Read from the live story, so adding or removing a blok in the Visual
  // Editor moves the section without a reload.
  const showsPathFallback =
    senjaWidgetId && senjaHeading && !hasSenjaBlok(content)
  const senjaSection = showsPathFallback ? (
    <div className="mx-auto w-[90%] max-w-5xl">
      <SenjaVideoTestimonials
        testimonials={senjaTestimonials ?? []}
        heading={senjaHeading}
        widgetID={senjaWidgetId}
      />
    </div>
  ) : null
  const reviewSegmentLink = reviewSegment ? (
    <ReviewSegmentServiceLink segment={reviewSegment} />
  ) : null
  const hasBeforeFooter = senjaSection !== null || reviewSegmentLink !== null

  return (
    <ReviewsContextProvider preview={preview}>
      <PageHead
        title={content.title ?? ""}
        description={content.description ?? ""}
        customScript={content.customScript}
        richSnippets={[]}
      >
        <meta name={STORYBLOK_PAGE_MARKER} content="true" />
        {content.richSnippets?.map((item, n) => {
          const jsonLd = isCatPage
            ? sanitizeCatReviewSnippet(item.content)
            : sanitizeStoryblokRichSnippetContent(item.content)

          // Unparseable CMS content is dropped rather than shipped raw:
          // Googlebot parses JSON-LD strictly and would reject the block anyway.
          if (jsonLd === null) {
            return null
          }

          return (
            <script
              key={`application/ld+json${n}`}
              id={`application/ld+json${n}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: jsonLd }}
            />
          )
        })}
        {isHomePage ? (
          <meta name="x:creator:id" content="1467726470533754880" />
        ) : null}
      </PageHead>
      <SenjaTestimonialsContext.Provider
        value={senjaByGroup ?? NO_BLOK_TESTIMONIALS}
      >
        <CatCustomerRatingContext.Provider value={catRating}>
          <StoryblokComponent
            blok={content}
            beforeFooter={
              hasBeforeFooter ? (
                <>
                  {senjaSection}
                  {reviewSegmentLink}
                </>
              ) : undefined
            }
          />
        </CatCustomerRatingContext.Provider>
      </SenjaTestimonialsContext.Provider>
    </ReviewsContextProvider>
  )
}

export default Page

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  }
}

export const getStaticProps: GetStaticProps<Props> = async ({
  params,
  preview = false,
}) => {
  const slugSegments = Array.isArray(params?.slug) ? params.slug : []
  const isHomePage = slugSegments.length === 0

  if (shouldRedirect(slugSegments)) {
    return {
      redirect: {
        destination: getRedirectDestination(slugSegments),
        permanent: true,
      },
    }
  }

  const slug = isHomePage ? "index" : slugSegments.join("/")

  if (slug.includes(".map") || slug.includes("_next/")) {
    return {
      notFound: true,
    }
  }

  const senjaGroup = senjaGroupForPath(`/${slug}`)
  // Started before the Storyblok fetch so the two overlap. Memoized
  // process-wide and never rejects, so a Senja outage costs this page its
  // testimonial section and nothing else, and a Storyblok 404 leaves no
  // unhandled rejection behind.
  const pendingSenja = senjaGroup
    ? getSenjaTestimonialsForCachedRender(senjaGroup)
    : null

  try {
    const { story, key } = await fetchStoryblokPage(slug, preview)

    if (!hasPageStoryContent(story)) {
      throw new Error(`Invalid Storyblok page payload for slug: ${slug}`)
    }

    story.content.richSnippets?.forEach((item, index) => {
      sanitizeStoryblokRichSnippetContent(item.content, () => {
        process.emitWarning("Dropped malformed Storyblok JSON-LD", {
          code: "INVALID_STORYBLOK_JSON_LD",
          detail: `Story ${slug}, rich snippet ${index + 1}`,
        })
      })
    })

    const isSegmentService = REVIEW_SEGMENTS.some(
      ({ servicePath }) => servicePath === `/${slug}`,
    )
    let reviewSegment: ReviewSegmentDefinition | null = null

    if (isSegmentService) {
      const { getEnabledReviewSegments } = await import(
        "@/lib/reviews/segment-loader"
      )

      const matchingSegment = getEnabledReviewSegments().find(
        ({ servicePath }) => servicePath === `/${slug}`,
      )

      reviewSegment = matchingSegment ?? null
    }

    const concept = getShipmentConcept(slug)
    const isCatPage = isCatReviewPage(slug, story.id)
    const reviewFetchOptions = isCatPage ? { fresh: true } : undefined
    const [shipmentCopyReviews, completedShipments] = concept
      ? await Promise.all([
          fetchReviews(reviewFetchOptions),
          getCompletedShipmentStats(),
        ])
      : [null, null]
    // A story that places the blok controls the section's position and
    // heading itself, so the path's before-footer fallback is skipped and the
    // section never renders twice. The cache coalesces a blok group that
    // matches the path's group with the request already in flight.
    const { groups: blokGroups, hasBlok: usesSenjaBlok } = inspectSenjaStory(
      story.content,
    )
    // Draft pages preload every group so bloks added in the Visual Editor
    // render without a reload, and keep the path fallback for live removal.
    const isDraft = preview || isDraftStoryblokMode()
    const requestedGroups = isDraft
      ? (Object.keys(SENJA_WIDGET_GROUPS) as SenjaWidgetGroupKey[])
      : blokGroups
    const needsPathFallback = !usesSenjaBlok || isDraft
    const providesBlokGroups = usesSenjaBlok || isDraft
    const blokBatches = await Promise.all(
      requestedGroups.map((group) =>
        getSenjaTestimonialsForCachedRender(group),
      ),
    )
    const senja = needsPathFallback && pendingSenja ? await pendingSenja : null
    const senjaBatches = [...blokBatches, senja].filter(
      (batch) => batch !== null,
    )
    const revalidate = pageRevalidateSeconds(
      isSegmentService,
      senjaBatches,
      Boolean(concept || hasExperienceYearsOnly(slug)),
    )

    return {
      ...(revalidate === undefined ? {} : { revalidate }),
      props: {
        ...(isCatPage
          ? { catCustomerRating: parseCatCustomerRating(shipmentCopyReviews) }
          : {}),
        completedShipments,
        story: {
          ...story,
          content: resolveStoryContent(
            story.content,
            slug,
            completedShipments,
            shipmentCopyReviews,
          ),
        },
        storySlug: slug,
        key,
        preview: preview || false,
        isHomePage,
        ...(reviewSegment ? { reviewSegment } : {}),
        ...(providesBlokGroups
          ? {
              senjaByGroup: Object.fromEntries(
                requestedGroups.map((group, index) => [
                  group,
                  blokBatches[index].testimonials,
                ]),
              ),
            }
          : {}),
        ...(senjaGroup && senja
          ? {
              senjaTestimonials: senja.testimonials,
              senjaWidgetId: SENJA_WIDGET_GROUPS[senjaGroup].widgetId,
              senjaHeading: SENJA_WIDGET_GROUPS[senjaGroup].heading,
            }
          : {}),
      },
    }
  } catch (error) {
    if (!isStoryblokNotFoundError(error)) {
      throw error
    }

    return {
      notFound: true,
    }
  }
}
